import { bearerFetcher, useAuthBearerFetch, useAuthFetch } from "matsuri-hooks"
import { useMemo } from "react"
import dayjs from "dayjs"
import { datadogLogs } from "@datadog/browser-logs"
import { m2mCleaning_managerV4 } from "../endpoints/m2m-cleaning.manager-v4"
import {
    Cleaning,
    CleaningInfoDetail,
    CleaningType,
    CleaningWithCleaners
} from "./../domain/cleaning"
import { decodeJwt } from "./useAccountInfo"
import { Cleaner } from "@/domain/user"
import { GarbageStorageCheckForManager } from "@/domain/garbageStorageCheck"
import { PlacementType } from "@/domain/placement"
import { m2mCleaning_managerV3 } from "@/endpoints/m2m-cleaning.manager-v3"
import { useAuth } from "@/hooks/useAuth"

export interface CreateCleaningInput {
    cleaningDate: string
    cleaners: string[]
    listingId: string
    placement?: string
    note: string | undefined
    scheduleStart?: number
    scheduleEnd?: number
}

interface CreateDelegateCleaningInput {
    cleaningDate: string
    listingId: string
    note: string | undefined
    placement: string | undefined
}

export interface CreateCleaningWithPlacementInput {
    listingId?: string
    commonAreaId?: string
    cleaningDate: string
    note?: string
    placement: PlacementType
    cleaners: string[]
    photoTourId?: string
    submissionId?: string
    scheduleStart?: number
    scheduleEnd?: number
    reservationId?: string
    /** SKU 複数選択。BE: m2m-cleaning#4592 の CleaningSkuInput に対応 */
    skus?: { commodityId: string; quantity: number }[]
}

export const requestCreateCleaning = (
    token: string,
    input: CreateCleaningInput
) => {
    return bearerFetcher<{ cleaningId: string }>(
        token,
        m2mCleaning_managerV3.createCleaning(),
        {
            method: "POST",
            body: JSON.stringify(input)
        }
    )
}

export const requestCreateDelegateCleaning = (
    token: string,
    input: CreateDelegateCleaningInput
) => {
    return bearerFetcher<{ cleaningId: string }>(
        token,
        m2mCleaning_managerV3.createCleaningOfListing({
            listingId: input.listingId
        }),
        {
            method: "POST",
            body: JSON.stringify(input)
        }
    )
}

interface CreateErrandInput {
    title: string
    description: string
}

export const requestCreateErrand = async (
    token: string,
    cleaningId: string,
    input: CreateErrandInput
) => {
    return bearerFetcher(
        token,
        m2mCleaning_managerV3.createErrandsByListingManager({ cleaningId }),
        {
            method: "POST",
            body: JSON.stringify(input)
        }
    )
}

export const requestDeleteErrand = async (
    token: string,
    cleaningId: string,
    errandId: string
) => {
    return bearerFetcher(
        token,
        m2mCleaning_managerV3.deleteErrandByListingManager({
            cleaningId,
            errandId
        }),
        {
            method: "DELETE"
        }
    )
}

export const requestUpdateCleaning = async (
    token: string,
    cleaningId: string,
    input: {
        note?: string
        submissionId?: string
        /** SKU 連動数量更新 (m2m-cleaning#4655 の UpdateCleaningInput に対応) */
        skus?: { commodityId: string; quantity: number }[]
    }
) => {
    return bearerFetcher(
        token,
        m2mCleaning_managerV3.updateCleaning({ cleaningId }),
        {
            method: "PUT",
            body: JSON.stringify(input)
        }
    )
}

export const requestUpdateCleaningAvailability = async (
    token: string,
    cleaningId: string,
    input: { isDisabled?: boolean }
) => {
    return bearerFetcher(
        token,
        m2mCleaning_managerV3.updateCleaning({ cleaningId }),
        {
            method: "PUT",
            body: JSON.stringify(input)
        }
    )
}

export const useCleaning = (token: string, cleaningId: string | undefined) => {
    return useAuthFetch<CleaningInfoDetail>(
        token,
        cleaningId ? m2mCleaning_managerV3.getCleaning({ cleaningId }) : null,
        {
            authTokenHeader: "Authorization"
        }
    )
}

/**
 * 清掃のゴミストッカー溢れチェック報告を取得する（管理画面向け）。
 * isTarget で報告対象かを、check で報告内容/未報告（null）を判別する。
 */
export const useGarbageStorageCheck = (
    token: string,
    cleaningId: string | undefined
) => {
    return useAuthFetch<GarbageStorageCheckForManager>(
        token,
        cleaningId
            ? m2mCleaning_managerV4.getGarbageStorageCheck({ cleaningId })
            : null,
        {
            authTokenHeader: "Authorization"
        }
    )
}

export const requestAssignCleaner = (
    token: string,
    cleaningId: string,
    cleanerId: string,
    fixedPrice?: number
) => {
    // アサインの様子を知りたいので検証の一環でログを仕込みます。
    // 十分な時間が経ったら(例えば2025/03/10以降とか)には削除しても構いません
    datadogLogs.logger.info("requestAssignCleaner", {
        context: {
            requestUserId: decodeJwt(token)?.userId,
            cleaningId,
            cleanerId,
            fixedPrice
        }
    })

    return bearerFetcher(
        token,
        m2mCleaning_managerV3.assignCleaners({ cleaningId }),
        {
            method: "POST",
            body: JSON.stringify({
                cleanerId,
                unitPrice: fixedPrice
            })
        }
    )
}

export const requestUnassignCleaner = (
    token: string,
    cleaningId: string,
    cleanerId: string
) => {
    datadogLogs.logger.info("requestUnassignCleaner", {
        context: {
            requestUserId: decodeJwt(token)?.userId,
            cleaningId,
            cleanerId
        }
    })

    return bearerFetcher(
        token,
        m2mCleaning_managerV3.unassignCleaners({ cleaningId }),
        {
            method: "DELETE",
            body: JSON.stringify({
                cleanerId
            })
        }
    )
}

export const requestUnassignAllCleaner = (
    token: string,
    cleaningId: string
) => {
    datadogLogs.logger.info("requestUnassignAllCleaner", {
        context: {
            requestUserId: decodeJwt(token)?.userId,
            cleaningId
        }
    })

    const endpoint = m2mCleaning_managerV4.unassignAllCleanerByCleaningId
    return bearerFetcher(token, endpoint({ cleaningId }), {
        method: endpoint.method
    })
}

export const requestAssignCompany = (
    token: string,
    cleaningId: string,
    companyId: string
) => {
    datadogLogs.logger.info("requestAssignCompany", {
        context: {
            requestUserId: decodeJwt(token)?.userId,
            cleaningId,
            companyId
        }
    })

    return bearerFetcher(
        token,
        m2mCleaning_managerV4.assignCompany({ cleaningId }),
        {
            method: "POST",
            body: JSON.stringify({ companyId })
        }
    )
}

export const requestSetInspectionByManager = (
    token: string,
    cleaningId: string,
    hasInspection: boolean
) => {
    const endpoint = m2mCleaning_managerV4.setInspectionByManager
    return bearerFetcher(token, endpoint({ cleaningId }), {
        method: endpoint.method,
        body: JSON.stringify({ hasInspection })
    })
}

export const requestUnassignCompany = (token: string, cleaningId: string) => {
    datadogLogs.logger.info("requestUnassignCompany", {
        context: {
            requestUserId: decodeJwt(token)?.userId,
            cleaningId
        }
    })

    return bearerFetcher(
        token,
        m2mCleaning_managerV4.unassignCompany({ cleaningId }),
        {
            method: "DELETE"
        }
    )
}

export const useCleaningCleaners = (
    token: string,
    cleaningId: string | undefined
) => {
    return useAuthBearerFetch<Cleaner[]>(
        token,
        cleaningId
            ? m2mCleaning_managerV3.listAssignedCleaners({ cleaningId })
            : null,
        {}
    )
}

export const requestGetAssignedCleanersByCleaningId = (
    token: string,
    cleaningId: string
) => {
    return bearerFetcher<Cleaner[]>(
        token,
        m2mCleaning_managerV3.listAssignedCleaners({ cleaningId }),
        {
            method: "GET"
        }
    )
}

export const useDelegateCleaning = (
    token: string,
    cleaningId: string | undefined
) => {
    return useAuthBearerFetch<CleaningInfoDetail>(
        token,
        cleaningId
            ? m2mCleaning_managerV3.getDelegateCleaning({ cleaningId })
            : null,
        {}
    )
}

export const requestTransferCleanings = async (
    token: string,
    input: {
        listingId: string
        sourceCompanyId: string
        targetCompanyId: string
        date: string
    }
) => {
    return bearerFetcher(token, m2mCleaning_managerV3.transferCleanings(), {
        method: "POST",
        body: JSON.stringify(input)
    })
}

export const requestTransferCleaning = async (
    token: string,
    input: {
        cleaningId: string
        targetCompanyId: string
    }
) => {
    return bearerFetcher(token, m2mCleaning_managerV3.transferCleaning(), {
        method: "POST",
        body: JSON.stringify(input)
    })
}

export interface CleaningSearchResponse extends CleaningWithCleaners {
    listingName: string
    cleanerNames: string[]
    commonAreaName?: string
    companyName?: string
    hasCheckinOnDate: boolean
    unresolvedTroubles: number
    submissionId?: string
    cleanerIds: string[]
    address: string
    cleaningCompanyId?: string
    cleaningCompanyName?: string
    subStatus: import("@/domain/cleaningSubStatus").SubStatus
}

interface UseSearchCleaningsInput {
    startDate?: string
    endDate?: string
    listingName?: string
    cityGroupId?: string
    isAssigned?: boolean
}

export const useSearchCleanings = (
    input: UseSearchCleaningsInput | undefined
) => {
    const { token } = useAuth()
    const endpoint = m2mCleaning_managerV4.searchCleanings
    return useAuthBearerFetch<CleaningSearchResponse[]>(
        token,
        input ? endpoint() : null,
        {
            body: input ? JSON.stringify(input) : undefined,
            method: endpoint.method
        }
    )
}

export const useSearchDelegateCleanings = (
    startDate?: string,
    endDate?: string,
    listingName?: string
) => {
    const { token } = useAuth()
    const { data, refetch } = useAuthBearerFetch<CleaningSearchResponse[]>(
        token,
        m2mCleaning_managerV3.searchDelegateCleanings(),
        {
            body: JSON.stringify({
                ...(listingName && {
                    listingName: listingName
                }),
                ...(startDate && {
                    startDate: startDate
                }),
                ...(endDate && {
                    endDate: endDate
                })
            }),
            method: "POST"
        }
    )

    return { data: data ?? [], refetch }
}

export const requestUpdateCleaningByListingManager = (
    token: string,
    cleaningId: string,
    input: {
        cleaningDate?: string
        listingManagerMemo?: string
        cleaningType?: CleaningType
        isDisabled?: boolean
    }
) => {
    return bearerFetcher(
        token,
        m2mCleaning_managerV3.updateCleaningByListingManager({ cleaningId }),
        {
            method: "PUT",
            body: JSON.stringify(input)
        }
    )
}

export const requestUpdateCleaningByCleaningCompany = (
    token: string,
    cleaningId: string,
    input: {
        cleaningCompanyMemo?: string
    }
) => {
    return bearerFetcher(
        token,
        m2mCleaning_managerV4.updateCleaningByCleaningCompany({ cleaningId }),
        {
            method: "PUT",
            body: JSON.stringify(input)
        }
    )
}

export const requestFindCleaningsByOtaId = (
    token: string,
    req: { otaId: string }
) => {
    return bearerFetcher<CleaningWithCleaners[]>(
        token,
        m2mCleaning_managerV3.findCleaningsByOtaId(),
        {
            method: "POST",
            body: JSON.stringify(req)
        }
    )
}

export const useCleaningsByListingId = (
    token: string,
    listingId: string | undefined,
    span: { startDate: string; endDate: string }
) => {
    return useAuthBearerFetch<Cleaning[]>(
        token,
        listingId
            ? m2mCleaning_managerV3.findCleaningsByListingId({ listingId })
            : null,
        {
            method: "POST",
            body: JSON.stringify({
                listingId,
                ...span
            })
        }
    )
}

// 3ヶ月以内の直前の清掃を見つける
export const usePreviousCleaningIn3Months = (
    token: string,
    listingId: string | undefined,
    cleaningDate: string | undefined
) => {
    const { data } = useAuthBearerFetch<Cleaning[]>(
        token,
        listingId && cleaningDate
            ? m2mCleaning_managerV3.findCleaningsByListingId({ listingId })
            : null,
        {
            method: "POST",
            body: JSON.stringify({
                listingId,
                startDate: dayjs(cleaningDate)
                    .add(-3, "month")
                    .format("YYYY-MM-DD"),
                endDate: dayjs(cleaningDate).add(-1, "day").format("YYYY-MM-DD")
            })
        }
    )
    const dataMemoized = useMemo(
        () => (data ? data[data.length - 1] : undefined),
        [data]
    )

    return {
        data: dataMemoized
    }
}

// 3ヶ月以内の直後の清掃を見つける
export const useNextCleaningIn3Months = (
    token: string,
    listingId: string | undefined,
    cleaningDate: string | undefined
) => {
    const { data } = useAuthBearerFetch<Cleaning[]>(
        token,
        listingId && cleaningDate
            ? m2mCleaning_managerV3.findCleaningsByListingId({ listingId })
            : null,
        {
            method: "POST",
            body: JSON.stringify({
                listingId,
                startDate: dayjs(cleaningDate)
                    .add(1, "day")
                    .format("YYYY-MM-DD"),
                endDate: dayjs(cleaningDate)
                    .add(3, "month")
                    .format("YYYY-MM-DD")
            })
        }
    )
    const dataMemoized = useMemo(() => (data ? data[0] : undefined), [data])

    return {
        data: dataMemoized
    }
}

export const requestUploadEvidence = (
    token: string,
    cleaningId: string,
    input: { data: string }
) => {
    return bearerFetcher(
        token,
        m2mCleaning_managerV3.uploadEvidencePictureByListingManager({
            cleaningId
        }),
        {
            method: "POST",
            body: JSON.stringify(input)
        }
    )
}

export const requestDeleteEvidence = (
    token: string,
    cleaningId: string,
    input: {
        imageId: string
    }
) => {
    return bearerFetcher(
        token,
        m2mCleaning_managerV3.deleteEvidencePictureByListingManager({
            cleaningId
        }),
        {
            method: "DELETE",
            body: JSON.stringify(input)
        }
    )
}

export const requestCreateCleaningWithPlacement = (
    token: string,
    input: CreateCleaningWithPlacementInput
) => {
    return bearerFetcher<string>(
        token,
        m2mCleaning_managerV3.createCleaningWithPlacement(),
        {
            token,
            method: "POST",
            body: JSON.stringify(input)
        }
    )
}

export const requestBatchCreateCleaningWithPlacement = async (
    token: string,
    requests: CreateCleaningWithPlacementInput[]
) => {
    return bearerFetcher<{ createdCleaningIds: string[] }>(
        token,
        m2mCleaning_managerV3.batchCreateCleaningWithPlacement(),
        {
            method: "POST",
            body: JSON.stringify({ cleanings: requests })
        }
    )
}

export const useSearchCleaningsWithPhotoTourIds = (
    token: string,
    input:
        | {
              startDate: string
              endDate: string
              photoTourIds: string[]
              cleaningStatus?: string | null
              cityGroupId?: string
          }
        | undefined
) => {
    return useAuthBearerFetch<CleaningSearchResponse[]>(
        token,
        input ? m2mCleaning_managerV4.findCleaningsWithPhotoTourIds() : null,
        {
            body: JSON.stringify(input),
            method: "POST"
        }
    )
}

interface UpdateCleaningScheduleInput {
    cleaningId: string
    scheduleStart?: number
    scheduleEnd?: number
}

export const requestUpdateCleaningSchedule = (
    token: string,
    { cleaningId, scheduleStart, scheduleEnd }: UpdateCleaningScheduleInput
) => {
    const endpoint = m2mCleaning_managerV4.updateCleaningSchedule

    return bearerFetcher(token, endpoint(), {
        method: endpoint.method,
        body: JSON.stringify({
            cleaningId,
            scheduleStart,
            scheduleEnd
        })
    })
}

export interface BulkUpdateListingCleaningTaskRelationInput {
    items: {
        listingId: string
        cleaningTaskId: string
        enable: boolean
    }[]
}

export const requestBulkUpdateListingCleaningTaskRelations = async (
    token: string,
    input: BulkUpdateListingCleaningTaskRelationInput
) => {
    return bearerFetcher<void>(
        token,
        m2mCleaning_managerV4.bulkUpdateListingCleaningTaskRelations(),
        {
            method: "POST",
            body: JSON.stringify(input)
        }
    )
}

interface UpdateCleaningDeadlineInput {
    cleaningId: string
    deadlineAt: number | null
}

export const requestUpdateCleaningDeadline = (
    token: string,
    { cleaningId, deadlineAt }: UpdateCleaningDeadlineInput
) => {
    const endpoint = m2mCleaning_managerV4.updateCleaningDeadline

    return bearerFetcher(token, endpoint({ cleaningId }), {
        method: endpoint.method,
        body: JSON.stringify({
            deadlineAt
        })
    })
}
